/**
 * Provider-specific message types and schemas
 */

import { z } from "zod";
import { SlackMessageSchema } from "./slack";
import { OutlookMessageSchema } from "./outlook";
import { GmailMessageSchema } from "./gmail";
import { GenericMessageSchema } from "./generic";

export * from "./slack";
export * from "./outlook";
export * from "./gmail";
export * from "./generic";

/**
 * Discriminated union schema for all provider-specific messages
 */
export const ProviderMessageSchema = z.discriminatedUnion("provider", [
  SlackMessageSchema,
  OutlookMessageSchema,
  GmailMessageSchema,
]);

export type ProviderMessage = z.infer<typeof ProviderMessageSchema> | z.infer<typeof GenericMessageSchema>;

/**
 * Type guard to check if a message is a provider-specific message
 */
export function isProviderMessage(message: any): message is ProviderMessage {
  return (
    message?.provider &&
    (message.provider === "slack-oauth" ||
      message.provider === "outlook-oauth" ||
      message.provider === "google-mail" ||
      message.provider === "communication" ||
      message.provider === "professional-email")
  );
}

/**
 * Validate a provider-specific message
 */
export function validateProviderMessage(data: unknown): ProviderMessage {
  // Try to use discriminated union first for core providers
  const coreResult = ProviderMessageSchema.safeParse(data);
  if (coreResult.success) return coreResult.data;

  // Fall back to generic message for others
  return GenericMessageSchema.parse(data);
}
